const STORAGE_KEY = 'tapanda_retention_offer_v1';
const DURATION = 2 * 60 * 60 * 1000;
const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function parseIssuedOffer(payload) {
  if (!payload || payload.percent !== 15 || typeof payload.code !== 'string' || !/^TP15-[A-Z0-9-]{6,80}$/i.test(payload.code)) throw new Error('Nie udało się potwierdzić rabatu. Spróbuj ponownie.');
  const issued = Date.parse(payload.issued_at);
  const expires = Date.parse(payload.expires_at);
  const server = Date.parse(payload.server_now);
  if (![issued, expires, server].every(Number.isFinite) || expires <= issued || Math.abs(expires - issued - DURATION) > 1000 || issued > server + 5000) throw new Error('Nie udało się potwierdzić ważności rabatu. Spróbuj ponownie.');
  return { code: payload.code.toUpperCase(), percent: 15, issued_at: new Date(issued).toISOString(), expires_at: new Date(expires).toISOString(), server_now: new Date(server).toISOString() };
}

export function formatRemaining(milliseconds) {
  const seconds = Math.max(0, Math.ceil(milliseconds / 1000));
  return [Math.floor(seconds / 3600), Math.floor(seconds % 3600 / 60), seconds % 60].map(number => String(number).padStart(2, '0')).join(':');
}

export function cloneConfiguredPreview(doc, source, prefix) {
  if (!source) return null;
  const clone = source.cloneNode(true);
  const sourceNodes = [source, ...source.querySelectorAll('*')];
  const clonedNodes = [clone, ...clone.querySelectorAll('*')];
  const getStyle = doc.defaultView?.getComputedStyle;
  for (const [index, original] of sourceNodes.entries()) {
    const copied = clonedNodes[index];
    if (!getStyle || original.namespaceURI !== 'http://www.w3.org/1999/xhtml') continue;
    const computed = getStyle.call(doc.defaultView, original);
    for (const property of ['display', 'position', 'width', 'height', 'top', 'left', 'right', 'bottom', 'transform', 'transform-origin', 'opacity', 'mix-blend-mode', 'object-fit', 'object-position', 'background-image', 'background-size', 'background-position', 'background-repeat', 'overflow', 'box-sizing', 'clip-path', 'mask', 'mask-image', 'filter']) {
      const value = computed.getPropertyValue(property);
      if (value) copied.style.setProperty(property, value);
    }
  }
  clone.querySelectorAll('script, iframe, button, input, textarea, select, [contenteditable], .tee-image--loader, .tee-loading').forEach(node => node.remove());
  const sourceCanvases = [...source.querySelectorAll('canvas')];
  for (const [index, canvas] of [...clone.querySelectorAll('canvas')].entries()) {
    try { canvas.getContext('2d').drawImage(sourceCanvases[index], 0, 0); } catch { return null; }
  }
  // A live Teeinblue <use> often points at a separate SVG outside the slide.
  const references = new Set();
  for (let pass = 0; pass < 8; pass += 1) {
    const missing = [];
    for (const node of [clone, ...clone.querySelectorAll('*')]) {
      for (const attribute of [...node.attributes]) {
        if (['href', 'xlink:href'].includes(attribute.name) && attribute.value.startsWith('#') && !references.has(attribute.value)) missing.push({ node, reference: attribute.value });
        for (const match of attribute.value.matchAll(/url\(\s*['"]?#([^)'"\s]+)['"]?\s*\)/g)) if (!references.has(`#${match[1]}`)) missing.push({ node, reference: `#${match[1]}` });
      }
    }
    if (!missing.length) break;
    for (const { node, reference } of missing) {
      if (references.has(reference)) continue;
      references.add(reference);
      const id = reference.slice(1);
      if ([...clone.querySelectorAll('[id]')].some(node => node.id === id)) continue;
      const original = doc.getElementById(id);
      if (!original) return null;
      const svg = node.closest('svg') || clone.querySelector('svg');
      if (!svg) return null;
      const defs = doc.createElementNS('http://www.w3.org/2000/svg', 'defs');
      defs.append(original.cloneNode(true));
      svg.prepend(defs);
    }
  }
  const all = [clone, ...clone.querySelectorAll('*')];
  const ids = new Map();
  for (const node of all) if (node.id) { const old = node.id; const next = `${prefix}-${ids.size}`; ids.set(old, next); node.id = next; }
  for (const node of all) {
    for (const attribute of [...node.attributes]) {
      if (/^on/i.test(attribute.name) || attribute.name === 'tabindex' || attribute.name === 'autofocus') { node.removeAttribute(attribute.name); continue; }
      let value = attribute.value;
      for (const [old, next] of ids) {
        if (value === `#${old}`) value = `#${next}`;
        if (attribute.name === 'aria-labelledby' || attribute.name === 'aria-describedby') value = value.split(' ').map(part => part === old ? next : part).join(' ');
      }
      value = value.replace(/url\(\s*['"]?#([^)'"\s]+)['"]?\s*\)/g, (match, id) => ids.has(id) ? `url(#${ids.get(id)})` : match);
      if (value !== attribute.value) node.setAttribute(attribute.name, value);
    }
    if (node.localName === 'img') { node.removeAttribute('loading'); node.decoding = 'async'; }
  }
  clone.setAttribute('aria-hidden', 'true');
  clone.classList.add('tp-retention__project');
  return clone;
}

export function createRetentionOffer(config, env = window) {
  const doc = env.document;
  const fetcher = env.fetch.bind(env);
  let record = null;
  let status = 'idle';
  let message = '';
  let needsRetry = false;
  let verified = false;
  let serverAnchor = 0;
  let monotonicAnchor = 0;
  let highWater = 0;
  let inFlight = null;
  let refreshAfterFlight = false;
  let verification = Promise.resolve();
  let cartQueue = Promise.resolve();
  let timer = null;
  let badge = null;
  let badgeFrame = null;
  let previewSerial = 0;
  let destroyed = false;
  const cleanups = [];
  const monotonic = () => env.performance.now();
  const storage = [];
  for (const name of ['localStorage', 'sessionStorage']) { try { if (env[name]) storage.push(env[name]); } catch { /* Storage may be blocked by browser settings. */ } }

  function readRecord() {
    for (const store of storage) {
      try {
        const value = JSON.parse(store.getItem(STORAGE_KEY) || 'null');
        if (value?.version === 1 && UUID_V4.test(value.activation_id) && value.attempted === true) return value;
      } catch { /* Another storage surface may still be available. */ }
    }
    return null;
  }

  function persist() {
    let stored = false;
    if (!record) return false;
    for (const store of storage) {
      try { const serialized = JSON.stringify(record); store.setItem(STORAGE_KEY, serialized); stored ||= store.getItem(STORAGE_KEY) === serialized; } catch { /* Do not replace the existing activation ID. */ }
    }
    return stored;
  }

  function trustedNow() {
    if (!verified) return null;
    highWater = Math.max(highWater, serverAnchor + Math.max(0, monotonic() - monotonicAnchor));
    return highWater;
  }

  function getActive() {
    const now = trustedNow();
    if (!record?.offer || record.expired || now === null || now >= Date.parse(record.offer.expires_at)) return null;
    return { ...record.offer, remaining_ms: Math.max(0, Date.parse(record.offer.expires_at) - now), applied: record.applied === true };
  }

  function getSnapshot() {
    const active = getActive();
    return {
      status, code: record?.offer?.code || null, percent: record?.offer?.percent || 15,
      issued_at: record?.offer?.issued_at || null, expires_at: record?.offer?.expires_at || null,
      expired: record?.expired === true, applied: active?.applied === true,
      active, message, suppress_newsletter: !!record?.offer && !record.expired,
    };
  }

  function publish() {
    if (destroyed) return;
    const snapshot = getSnapshot();
    doc.documentElement.classList.toggle('tapanda-retention-active', snapshot.suppress_newsletter);
    render(snapshot);
    env.dispatchEvent(new env.CustomEvent('tapanda:retention-offer-state', { detail: snapshot }));
  }

  async function jsonRequest(url, options = {}) {
    const controller = new env.AbortController();
    const timeout = env.setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetcher(url, { ...options, signal: controller.signal, redirect: 'error', cache: 'no-store' });
      if (!response.ok) { const error = new Error('REQUEST_FAILED'); error.status = response.status; throw error; }
      return await response.json();
    } finally { env.clearTimeout(timeout); }
  }

  function expired() {
    if (!record) return;
    record.expired = true;
    record.applied = false;
    verified = false;
    status = 'expired';
    message = 'Oferta wygasła. Nadal możesz zamówić swój projekt.';
    persist();
    publish();
  }

  function cartRoot() { return ((env.Shopify?.routes?.root) || '/').replace(/\/?$/, '/'); }

  function reconcileCart({ extraCodes = [], userInitiated = false } = {}) {
    const priorGlobal = env.tapandaCartDiscountInFlight;
    const priorQuantity = env.tapandaCartQuantityInFlight;
    const priorManual = env.tapandaCartDiscountManualInFlight;
    const previous = cartQueue;
    const verificationBeforeCart = verification;
    const operation = Promise.allSettled([verificationBeforeCart, previous, priorGlobal, priorQuantity, priorManual].filter(value => value?.then)).then(async () => {
      if (env.tapandaCartDiscountManualPending) return { applied: false, cart: null, skipped: 'manual-pending' };
      let active = getActive();
      const ours = record?.offer?.code || null;
      if (!active && !ours && !extraCodes.length) return { applied: false, cart: null };
      const current = await jsonRequest(`${cartRoot()}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!Array.isArray(current.items) || !Array.isArray(current.discount_codes)) throw new Error('Nie udało się sprawdzić koszyka.');
      active = getActive();
      const existing = current.discount_codes.map(item => item?.code).filter(Boolean);
      let intent = env.tapandaCartDiscountManualIntent;
      if (!intent) { try { intent = JSON.parse(env.sessionStorage.getItem('tapanda_cart_discount_manual_intent_v1') || 'null'); } catch { /* Browser may block session storage. */ } }
      if (intent && !(Number(intent.expiresAt) > env.Date.now())) intent = null;
      const sameFingerprint = !intent?.cartFingerprint || !env.tapandaCartDiscountFingerprint || intent.cartFingerprint === env.tapandaCartDiscountFingerprint(current);
      const manuallySuppressed = !userInitiated && intent && ((intent.action === 'remove' && sameFingerprint) || (intent.action === 'apply' && intent.code && String(intent.code).toUpperCase() !== active?.code));
      const candidates = [...new Set([...existing, ...extraCodes, manuallySuppressed ? null : active?.code].filter(Boolean).map(code => String(code).trim().toUpperCase()))]
        .filter(code => !(ours && code === ours && record?.expired));
      const oldCodes = existing.map(code => String(code).trim().toUpperCase());
      const mustUpdate = candidates.length !== oldCodes.length || candidates.some(code => !oldCodes.includes(code));
      const cartElement = doc.querySelector('cart-items, cart-drawer-items');
      const sequence = cartElement?.tapandaCartRequestSequence;
      const sectionTargets = [];
      for (const id of ['main-cart-items', 'main-cart-footer']) {
        const node = doc.getElementById(id);
        if (node?.dataset.id) sectionTargets.push({ node, section: node.dataset.id, selectors: id === 'main-cart-items' ? ['.js-contents'] : ['.js-contents', '.tp-cart-combined__timeline', '.tp-pay-badges', '.tp-cart-xsell'] });
      }
      let updated = null;
      if (mustUpdate) {
        const body = { discount: candidates.join(',') };
        if (sectionTargets.length) { body.sections = [...new Set(sectionTargets.map(target => target.section))]; body.sections_url = env.location.pathname; }
        updated = await jsonRequest(`${cartRoot()}cart/update.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
      }
      const confirmed = await jsonRequest(`${cartRoot()}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!Array.isArray(confirmed.discount_codes)) throw new Error('Nie udało się potwierdzić rabatu w koszyku.');
      const applied = !!active && confirmed.discount_codes.some(item => String(item?.code || '').toUpperCase() === active.code && item.applicable === true);
      const sameCartState = updated && JSON.stringify([updated.items, updated.total_price, updated.discount_codes]) === JSON.stringify([confirmed.items, confirmed.total_price, confirmed.discount_codes]);
      if (sameCartState && updated.sections && (!cartElement || cartElement.tapandaCartRequestSequence === sequence)) {
        for (const target of sectionTargets) {
          if (typeof updated.sections[target.section] !== 'string') continue;
          const parsed = new env.DOMParser().parseFromString(updated.sections[target.section], 'text/html');
          for (const selector of target.selectors) {
            const currentNode = target.node.querySelector(selector), replacement = parsed.querySelector(selector);
            if (currentNode && replacement) currentNode.innerHTML = replacement.innerHTML;
          }
        }
      }
      if (mustUpdate) {
        env.dispatchEvent(new env.CustomEvent('tapanda:cart-updated', { detail: { cart: confirmed, source: 'retention_offer' } }));
        doc.dispatchEvent(new env.CustomEvent('cart:updated'));
      }
      if (record?.offer?.code === ours && getActive()?.code === ours && !record.expired) { record.applied = applied; status = applied ? 'active' : 'pending'; needsRetry = false; message = applied ? 'Rabat 15% jest aktywny w koszyku.' : 'Rabat 15% jest aktywny. Koszyk uwzględni dostępne promocje.'; persist(); publish(); }
      return { applied, cart: confirmed };
    });
    env.tapandaCartDiscountInFlight = operation;
    cartQueue = operation.catch(() => {});
    operation.finally(() => { if (env.tapandaCartDiscountInFlight === operation) env.tapandaCartDiscountInFlight = null; }).catch(() => {});
    return operation;
  }

  function uuid() {
    if (typeof env.crypto.randomUUID === 'function') return env.crypto.randomUUID();
    const bytes = env.crypto.getRandomValues(new Uint8Array(16)); bytes[6] = bytes[6] & 15 | 64; bytes[8] = bytes[8] & 63 | 128;
    const hex = [...bytes].map(number => number.toString(16).padStart(2, '0'));
    return `${hex.slice(0,4).join('')}-${hex.slice(4,6).join('')}-${hex.slice(6,8).join('')}-${hex.slice(8,10).join('')}-${hex.slice(10).join('')}`;
  }

  async function synchronize(activate = false) {
    if (inFlight) return inFlight;
    record = readRecord() || record;
    if (record?.expired) { expired(); return null; }
    if (!record && !activate) return null;
    if (!record) record = { version: 1, activation_id: uuid(), attempted: true, offer: null, expired: false, applied: false };
    if (!persist()) { status = 'error'; message = 'Nie można zapamiętać aktywacji. Zezwól na zapisywanie danych tej strony i spróbuj ponownie.'; publish(); return null; }
    const activationId = record.activation_id;
    let finishVerification;
    verification = new Promise(resolve => { finishVerification = resolve; });
    status = record.offer ? 'checking' : 'activating'; message = ''; needsRetry = false; publish();
    inFlight = Promise.resolve().then(async () => {
      try {
        if (!config.endpoint) throw new Error('Missing endpoint');
        const payload = await jsonRequest(config.endpoint, { method: 'POST', credentials: 'omit', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ activation_id: activationId }) });
        if (record?.activation_id !== activationId) { refreshAfterFlight = true; return null; }
        const offer = parseIssuedOffer(payload);
        if (record.expired) return null;
        if (record.offer && (record.offer.code !== offer.code || record.offer.expires_at !== offer.expires_at || record.offer.issued_at !== offer.issued_at)) throw new Error('Activation changed');
        record.offer = offer;
        verified = true; serverAnchor = Date.parse(offer.server_now); monotonicAnchor = monotonic(); highWater = Math.max(serverAnchor, record.high_water || 0);
        record.high_water = highWater;
        if (trustedNow() >= Date.parse(offer.expires_at)) { expired(); return null; }
        status = record.applied ? 'active' : 'pending'; message = 'Rabat 15% jest aktywny.'; persist(); publish();
        finishVerification();
        if (activate) {
          try { await reconcileCart({ userInitiated: true }); }
          catch { status = 'pending'; needsRetry = true; message = 'Rabat jest aktywny. Nie udało się potwierdzić naliczenia w koszyku.'; publish(); }
        }
        return getActive();
      } catch (error) {
        if (record?.activation_id !== activationId) { refreshAfterFlight = true; return null; }
        if (error.status === 410) { expired(); return null; }
        status = record.offer ? 'pending' : 'error';
        needsRetry = true;
        message = error.status === 429 ? 'Zbyt wiele prób. Poczekaj chwilę i spróbuj ponownie.'
          : error.status === 409 ? 'Aktywacja jeszcze trwa. Spróbuj ponownie za chwilę.'
            : record.offer ? 'Nie udało się sprawdzić rabatu. Spróbuj ponownie; czas oferty nie zmieni się.'
              : 'Nie udało się potwierdzić aktywacji. Spróbuj ponownie.';
        publish();
        return null;
      } finally {
        finishVerification();
        inFlight = null; scheduleTick();
        if (refreshAfterFlight && !destroyed) { refreshAfterFlight = false; void synchronize(false); }
      }
    });
    return inFlight;
  }

  function scheduleTick() {
    env.clearTimeout(timer); timer = null;
    if (destroyed || doc.hidden || !record?.offer || record.expired || !verified) return;
    const active = getActive();
    if (!active) { expired(); reconcileCart().catch(() => {}); return; }
    updateTimers(active.remaining_ms);
    timer = env.setTimeout(scheduleTick, Math.min(1000, Math.max(1, active.remaining_ms)));
  }

  function updateTimers(left) {
    const time = formatRemaining(left);
    const popupTimer = doc.querySelector('[data-retention-popup-timer]');
    if (popupTimer) { popupTimer.hidden = false; popupTimer.textContent = `Pozostało ${time}`; popupTimer.setAttribute('aria-live', 'off'); }
    const target = badge?.querySelector('[data-retention-countdown]');
    if (target) target.textContent = time;
  }

  function ensureBadge() {
    // Most SDK mutations are unrelated to discounts. Avoid measuring its price
    // (and forcing a layout) until a verified offer actually needs a badge.
    if (destroyed || !getActive()) { if (badge) badge.hidden = true; return; }
    const section = doc.querySelector('[id^="MainProduct-"]');
    const visible = node => {
      if (!node.getClientRects().length) return false;
      for (let current = node; current && current !== doc.documentElement; current = current.parentElement) {
        const style = env.getComputedStyle(current);
        if (style.display === 'none' || style.visibility === 'hidden') return false;
      }
      return true;
    };
    const price = [...section?.querySelectorAll('.tee-product-price') || []].find(visible)
      || [...section?.querySelectorAll('[id^="price-"]') || []].find(visible);
    if (!price) return;
    if (!badge?.isConnected) {
      badge = doc.createElement('div'); badge.className = 'tp-retention-price'; badge.hidden = true;
      badge.innerHTML = '<span class="tp-retention-price__percent">−15%</span><span class="tp-retention-price__copy">Twój rabat jest aktywny<span>Rabat rozliczany w koszyku</span></span><span class="tp-retention-price__time" role="timer" aria-live="off" aria-label="Pozostały czas rabatu" data-retention-countdown></span>';
    }
    if (badge.previousElementSibling !== price) price.after(badge);
  }

  function scheduleBadge() {
    if (destroyed || !getActive()) { if (badge) badge.hidden = true; return; }
    if (badgeFrame !== null) return;
    badgeFrame = env.requestAnimationFrame(() => {
      badgeFrame = null;
      if (destroyed) return;
      ensureBadge();
      if (badge) badge.hidden = !getActive();
    });
  }

  function render(snapshot) {
    const step = doc.querySelector('[data-tp-step="leave"]');
    const activateButton = step?.querySelector('[data-retention-activate]');
    const retryButton = step?.querySelector('[data-retention-retry]');
    const continueButton = step?.querySelector('[data-retention-continue]');
    const statusNode = step?.querySelector('[data-retention-status]');
    const description = step?.querySelector('.tp-np__desc');
    if (description) description.hidden = !!record?.offer || record?.expired === true;
    if (snapshot.expired) {
      const title = step?.querySelector('#tp-np-title-leave');
      if (title) title.textContent = 'Oferta wygasła';
    }
    const working = status === 'activating' || status === 'checking';
    if (activateButton) { activateButton.hidden = !!record?.offer || record?.expired === true; activateButton.disabled = working; activateButton.textContent = working ? 'Aktywowanie…' : 'Aktywuj rabat 15%'; }
    if (retryButton) { retryButton.hidden = !record?.offer || status !== 'pending' || !needsRetry; retryButton.disabled = working; retryButton.textContent = verified ? 'Sprawdź rabat w koszyku' : 'Sprawdź aktywację'; }
    if (continueButton) continueButton.hidden = !record?.offer && !record?.expired;
    if (statusNode) { if (statusNode.textContent !== message) statusNode.textContent = message; statusNode.dataset.tone = status === 'error' || status === 'expired' ? 'error' : 'success'; }
    ensureBadge();
    if (badge) { badge.hidden = !snapshot.active; if (snapshot.active) updateTimers(snapshot.active.remaining_ms); }
    const popupTimer = step?.querySelector('[data-retention-popup-timer]');
    if (popupTimer && !snapshot.active) popupTimer.hidden = true;
  }

  function renderPreview() {
    const host = doc.querySelector('[data-retention-preview-host]');
    const target = host?.querySelector('[data-retention-preview]');
    if (!host || !target) return;
    target.replaceChildren(); delete host.dataset.customerPreview; delete host.dataset.previewReady;
    const section = doc.querySelector('[id^="MainProduct-"]');
    const customerUrl = section?.getAttribute('data-tp-cc-gallery-url');
    const active = section?.querySelector('#tee-gallery .tee-slide--active .tee-mockup-item--loaded');
    // Show the printed design here; the product frame belongs to the gallery.
    const source = active?.querySelector('.tee-printarea--printarea svg.tee-printarea-artwork');
    const configured = !!customerUrl || [...section?.querySelectorAll('#teeFormInputs textarea, #teeFormInputs input[type="text"]') || []].some(input => input.value.trim());
    if (source) {
      const clone = cloneConfiguredPreview(doc, source, `tp-retention-preview-${++previewSerial}`);
      if (clone) {
        const box = String(source.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
        const naturalWidth = box.length === 4 ? box[2] : 0;
        const naturalHeight = box.length === 4 ? box[3] : 0;
        if (naturalWidth > 0 && naturalHeight > 0) {
          clone.dataset.nativeWidth = String(naturalWidth); clone.dataset.nativeHeight = String(naturalHeight);
          clone.style.width = `${naturalWidth}px`; clone.style.height = `${naturalHeight}px`;
          clone.setAttribute('preserveAspectRatio', 'xMidYMid meet');
          target.append(clone); host.dataset.previewReady = 'true';
          if (configured) host.dataset.customerPreview = 'true';
          fitPreview();
        }
      }
    }
    if (!host.dataset.previewReady && customerUrl) {
      const image = doc.createElement('img'); image.src = customerUrl; image.alt = ''; image.className = 'tp-retention__customer-image'; target.append(image); host.dataset.customerPreview = 'true'; host.dataset.previewReady = 'true';
    }
    host.toggleAttribute('data-configured-without-preview', configured && !host.dataset.previewReady);
  }

  function fitPreview() {
    const target = doc.querySelector('[data-retention-preview]');
    const clone = target?.querySelector('.tp-retention__project');
    if (!clone) return;
    const width = Number(clone.dataset.nativeWidth), height = Number(clone.dataset.nativeHeight);
    const availableWidth = target.clientWidth, availableHeight = target.clientHeight;
    if (!width || !height || !availableWidth || !availableHeight) return;
    const scale = Math.min(1, availableWidth / width, availableHeight / height);
    clone.style.position = 'absolute'; clone.style.transformOrigin = '0 0'; clone.style.transform = `scale(${scale})`;
    clone.style.left = `${(availableWidth - width * scale) / 2}px`; clone.style.top = `${(availableHeight - height * scale) / 2}px`;
  }

  function listen(target, event, listener, options) { target.addEventListener(event, listener, options); cleanups.push(() => target.removeEventListener(event, listener, options)); }
  listen(doc, 'click', event => {
    if (event.target.closest?.('[data-retention-activate]')) { event.preventDefault(); void synchronize(true); }
    if (event.target.closest?.('[data-retention-retry]')) { event.preventDefault(); if (inFlight) return; if (!verified) void synchronize(true); else void reconcileCart({ userInitiated: true }).catch(() => { status = 'pending'; message = 'Nie udało się sprawdzić koszyka. Spróbuj ponownie.'; publish(); }); }
  });
  listen(doc, 'tapanda:retention-popup-open', () => { renderPreview(); publish(); });
  listen(doc, 'visibilitychange', () => { if (doc.hidden) { env.clearTimeout(timer); timer = null; } else { scheduleTick(); if (record && !record.expired) void synchronize(false); } });
  listen(env, 'pageshow', () => { scheduleTick(); if (record && !record.expired) void synchronize(false); });
  listen(env, 'pagehide', () => { env.clearTimeout(timer); timer = null; if (record && verified) { record.high_water = trustedNow(); persist(); } });
  listen(env, 'resize', fitPreview, { passive: true });
  listen(env, 'storage', event => {
    if (event.key !== STORAGE_KEY) return;
    const next = readRecord();
    if (!next) return; // Removing browser data must not restart an existing offer.
    if (record?.activation_id === next.activation_id && record.expired === next.expired && record.applied === next.applied && record.offer?.code === next.offer?.code && record.offer?.expires_at === next.offer?.expires_at) return;
    if (record?.expired && record.activation_id === next.activation_id) next.expired = true;
    record = next; verified = false;
    if (record.expired) expired(); else { status = record.offer ? 'checking' : 'idle'; publish(); void synchronize(false); }
  });
  const section = doc.querySelector('[id^="MainProduct-"]');
  if (section && env.MutationObserver) {
    const observer = new env.MutationObserver(records => { if (records.some(entry => !badge?.contains(entry.target))) scheduleBadge(); });
    observer.observe(section, { childList: true, subtree: true }); cleanups.push(() => observer.disconnect());
  }
  record = readRecord();
  if (record?.expired) { status = 'expired'; message = 'Oferta wygasła. Nadal możesz zamówić swój projekt.'; }
  publish();
  const ready = record && !record.expired ? synchronize(false).then(async active => {
    if (active && !active.applied) {
      try { await reconcileCart(); }
      catch { status = 'pending'; needsRetry = true; message = 'Rabat jest aktywny. Nie udało się potwierdzić naliczenia w koszyku.'; publish(); }
    }
    return getActive();
  }) : Promise.resolve(null);
  // dispose is returned only to isolated tests; it is not exposed on window.
  return { publicApi: Object.freeze({ ready, getActive, getSnapshot, reconcileCart }), dispose() { destroyed = true; env.clearTimeout(timer); if (badgeFrame !== null) env.cancelAnimationFrame(badgeFrame); badgeFrame = null; cleanups.forEach(fn => fn()); } };
}

if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  const script = document.querySelector('script[data-tapanda-retention-offer]');
  if (script && !window.tapandaRetentionOffer) window.tapandaRetentionOffer = createRetentionOffer({ endpoint: script.dataset.endpoint }).publicApi;
}
